import { useQuery } from '@tanstack/react-query';

import { checkRecipeCreatorName, getCreator } from '../../../../api/recipe';
import { QueryKeys } from '../../../common/react-query/query-keys';
import useMeQuery from '../user/useMeQuery';

interface UseMyCreatorContentEmptyQueryParams {
  enabled: boolean;
}

/**
 * 내 크리에이터 페이지의 레시피·집밥일기 게시글이 모두 하나도 없는지 여부를 반환한다.
 * CreatorPage와 동일한 쿼리 키(getCreator → checkRecipeCreatorName)를 사용해 캐시를 공유하므로 추가 네트워크 호출이 발생하지 않는다.
 * 게시글 유무가 확정되기 전에는 `isMyCreatorContentEmpty`가 false이므로,
 * 확정 전후로 UI가 바뀌면 안 되는 호출부는 `isMyCreatorContentLoading`으로 판정 완료 여부를 확인해야 한다.
 */
export const useMyCreatorContentEmptyQuery = ({
  enabled,
}: UseMyCreatorContentEmptyQueryParams) => {
  const { me, isLoading: isMeLoading } = useMeQuery();

  const isCreator = !!me?.hasRecipeCreatorProfile;

  const { data: myCreatorInfo, isError: isMyCreatorInfoError } = useQuery({
    queryKey: ['getCreator'],
    queryFn: getCreator,
    enabled: enabled && isCreator,
  });

  const creatorName = myCreatorInfo?.name;

  const { data: creator, isError: isCreatorError } = useQuery({
    queryKey: QueryKeys.checkRecipeCreatorName(creatorName),
    queryFn: () => {
      if (!creatorName) return;

      return checkRecipeCreatorName(creatorName);
    },
    enabled: enabled && isCreator && !!creatorName,
  });

  const recipeCount = creator?.recipes.length ?? 0;
  const diaryCount = creator?.cookDiaries.length ?? 0;
  const isMyCreatorContentEmpty =
    !!creator && recipeCount === 0 && diaryCount === 0;

  // 조회에 실패하면 게시글 유무를 영영 확정할 수 없으므로 로딩을 해제해 호출부가 기본 동작으로 되돌아가게 한다
  const isQueryError = isMyCreatorInfoError || isCreatorError;
  // me 조회 전에는 크리에이터 여부조차 확정되지 않으므로 함께 로딩으로 본다
  const isMyCreatorContentLoading =
    enabled && (isMeLoading || (isCreator && !creator && !isQueryError));

  return { isMyCreatorContentEmpty, isMyCreatorContentLoading };
};
